import {
  createSelector,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";

import type { Location } from "@/libs/models";
import type { RootState } from "../../app/store";
import { LocationStatusTypes } from "../utils/types";

export type LocationQualityType = {
  locationBuffer: Location[];
  avgLocAccuracy: number;
  distanceWalked: number;
  biggestJump: number;
  countNoHasBearings: number;
  userNeedsToWalk: boolean;
  converged: boolean;
  hasWalked: boolean;
  goodARQuality: boolean;
};
// Restore if LocationQuality is needed again
const initialLocatioQuality = {
  avgLocAccuracy: -1.0,
  biggestJump: -1.0,
  converged: false,
  countNoHasBearings: 0,
  distanceWalked: -1.0,
  goodARQuality: false,
  hasWalked: false,
  locationBuffer: [] as Location[],
  userNeedsToWalk: true,
};

export interface SliceProps {
  // Location only updated when update thesholds are met
  currentLocation: Location | null;
  // Most updated location
  internalCurrentLocation: Location | null;
  status?: LocationStatusTypes;
  statusModalOpen: boolean;
  browserPermissionModalOpen: boolean;
  locationQuality: LocationQualityType;
}

export const initialState: SliceProps = {
  browserPermissionModalOpen: false,
  currentLocation: null,
  internalCurrentLocation: null,
  locationQuality: initialLocatioQuality,
  status: undefined,
  statusModalOpen: false,
};

export const LocationSlice = createSlice({
  initialState,
  name: "location",
  reducers: {
    setBrowserPermissionModalOpen: (
      state: SliceProps,
      action: PayloadAction<SliceProps["browserPermissionModalOpen"]>,
    ) => {
      state.browserPermissionModalOpen = action.payload;
    },
    setCurrentLocation: (
      state: SliceProps,
      action: PayloadAction<SliceProps["currentLocation"]>,
    ) => {
      state.currentLocation = action.payload;
    },
    setInternalCurrentLocation: (
      state: SliceProps,
      action: PayloadAction<SliceProps["currentLocation"]>,
    ) => {
      state.internalCurrentLocation = action.payload;
      const statusMatch = Object.values(LocationStatusTypes).find(
        (ls) => ls === action.payload?.status,
      );
      // if payload has valid status se it, elsewhere set positioning or outdoor
      if (action.payload?.status && statusMatch) {
        state.status = statusMatch;
        if (statusMatch === LocationStatusTypes.STOPPED) {
          state.internalCurrentLocation = null;
        }
      } else if (action.payload) {
        if (action.payload.floorId != -1)
          state.status = LocationStatusTypes.POSITIONING;
        else state.status = LocationStatusTypes.OUTDOOR;
      }
    },
    setLocationQuality: (
      state: SliceProps,
      action: PayloadAction<SliceProps["locationQuality"]>,
    ) => {
      state.locationQuality = action.payload;
    },
    setLocationStatus: (
      state: SliceProps,
      action: PayloadAction<SliceProps["status"]>,
    ) => {
      state.status = action.payload;
      if (action.payload === LocationStatusTypes.STOPPED) {
        state.currentLocation = null;
      }
    },
    setStatusModalOpen: (
      state: SliceProps,
      action: PayloadAction<SliceProps["statusModalOpen"]>,
    ) => {
      state.statusModalOpen = action.payload;
    },
  },
});

// Selectors
const sliceData = (state: RootState) => state.location;

export const selectCurrentLocation = createSelector(sliceData, (item) => {
  return item.currentLocation;
});

export const selectLocationStatus = createSelector(sliceData, (item) => {
  return item.status;
});

export const selectStatusModalOpen = createSelector(sliceData, (item) => {
  return item.statusModalOpen;
});

export const selectBrowserPermissionModalOpen = createSelector(
  sliceData,
  (item) => {
    return item.browserPermissionModalOpen;
  },
);

export const selectCurrentLocationFloorId = createSelector(
  sliceData,
  (item) => {
    return item.currentLocation?.floorId;
  },
);

export const selectCurrentLocationBuildingId = createSelector(
  sliceData,
  (item) => {
    return item.currentLocation?.buildingId;
  },
);

export const selectCurrentLocationStatus = createSelector(sliceData, (item) => {
  return item.status;
});

export const selectHasCurrentLocation = createSelector(sliceData, (item) => {
  return (
    item.currentLocation &&
    item.currentLocation !== null &&
    !!item.currentLocation?.buildingId &&
    item.currentLocation.buildingId > 0 &&
    !!item.currentLocation?.floorId &&
    item.currentLocation.floorId > 0
  );
});

export const selectHasUsableLocation = createSelector(sliceData, (item) => {
  return (
    item.currentLocation != null || item.status === LocationStatusTypes.OOB
  );
});

export const selectHasOutdoorLocation = createSelector(sliceData, (item) => {
  return (
    item.currentLocation &&
    item.currentLocation !== null &&
    (item.currentLocation.isOutdoor ||
      item.currentLocation.status === LocationStatusTypes.OUTDOOR)
  );
});

export const selectHasOOBLocation = createSelector(sliceData, (item) => {
  return (
    item.currentLocation &&
    item.currentLocation !== null &&
    item.currentLocation.status === LocationStatusTypes.OOB
  );
});

export const selectInternalCurrentLocation = createSelector(
  sliceData,
  (item) => {
    return item.internalCurrentLocation;
  },
);

export const selectLocationQuality = createSelector(sliceData, (item) => {
  return item.locationQuality;
});

// Action creators are generated for each case reducer function
export const {
  setCurrentLocation,
  setLocationStatus,
  setStatusModalOpen,
  setBrowserPermissionModalOpen,
  setLocationQuality,
  setInternalCurrentLocation,
} = LocationSlice.actions;

export default LocationSlice.reducer;
